import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { createCheckoutSession, fetchPlans, type Plan } from '@/lib/api'

function priceLabel(cents: number): string {
  return cents === 0 ? 'Free' : `$${(cents / 100).toFixed(0)}/mo`
}

export default function BillingPage() {
  const { user, isAuthenticated } = useAuth()
  const [params] = useSearchParams()
  const status = params.get('status')

  const { data: plans = [] } = useQuery({ queryKey: ['plans'], queryFn: fetchPlans })

  const checkout = useMutation({
    mutationFn: async (planKey: string) => {
      const { url, errors } = await createCheckoutSession(planKey)
      if (url) {
        window.location.href = url // hand off to Stripe Checkout
        return
      }
      throw new Error(errors[0] ?? 'Could not start checkout')
    },
  })

  return (
    <main className="mx-auto max-w-3xl px-6 py-12">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Plans</h1>
        <Link to="/" className="text-sm text-primary-600 hover:underline">← Back to editor</Link>
      </div>

      {status === 'success' && (
        <p className="mt-4 rounded-lg bg-green-100 px-4 py-2 text-sm text-green-800 dark:bg-green-900 dark:text-green-200">
          Subscription active — your credits have been topped up.
        </p>
      )}
      {status === 'cancelled' && (
        <p className="mt-4 rounded-lg bg-amber-100 px-4 py-2 text-sm text-amber-800 dark:bg-amber-900 dark:text-amber-200">
          Checkout cancelled.
        </p>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {plans.map((plan: Plan) => {
          const current = isAuthenticated && user?.plan === plan.key
          return (
            <div
              key={plan.key}
              className={`rounded-xl border p-5 ${
                current ? 'border-primary-500 ring-2 ring-primary-300 dark:ring-primary-700' : 'border-gray-200 dark:border-gray-800'
              } bg-white dark:bg-gray-900`}
            >
              <div className="text-lg font-semibold">{plan.name}</div>
              <div className="mt-1 text-2xl font-bold">{priceLabel(plan.priceCents)}</div>
              <div className="mt-1 text-sm text-gray-500">
                {plan.monthlyCreditAllowance.toLocaleString()} credits / month
              </div>

              <div className="mt-4">
                {current ? (
                  <span className="text-sm font-medium text-primary-600">Current plan</span>
                ) : plan.priceCents === 0 ? (
                  <span className="text-sm text-gray-400">—</span>
                ) : (
                  <button
                    onClick={() => checkout.mutate(plan.key)}
                    disabled={checkout.isPending}
                    className="btn-primary w-full text-sm"
                  >
                    {checkout.isPending ? '…' : 'Upgrade'}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {checkout.isError && (
        <p className="mt-4 text-sm text-red-500">{(checkout.error as Error).message}</p>
      )}
    </main>
  )
}
